import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import Tooltip from './Tooltip';

type StyleOptions = {
  fontSize: number;
};

const DEFAULT_FONT_SIZE = 14;

const useStyles = makeStyles<StyleOptions>()((theme, { fontSize }) => ({
  maxOneLine: {
    fontSize,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'start',
    fontFamily: theme.typography.fontFamily,
  },
  multipleLines: {
    fontSize,
    whiteSpace: 'initial',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    display: '-webkit-box',
    textAlign: 'start',
    fontFamily: theme.typography.fontFamily,
  },
}));

type OverflowTooltipProps = {
  text: string | null | undefined;
  children: React.ReactNode;
  maxLines?: number;
  className?: string;
  fontSize?: number;
};

const OverflowTooltip = ({
  text,
  children,
  maxLines = 1,
  fontSize = 14,
  className = '',
}: OverflowTooltipProps) => {
  const { classes, cx } = useStyles({ fontSize: fontSize || DEFAULT_FONT_SIZE });
  const textElementRef = useRef<HTMLDivElement>();
  const [hover, setHover] = useState(false);

  const compareSize = useCallback(() => {
    const { current } = textElementRef;
    let compare = current && current.scrollWidth > current.clientWidth;
    if (maxLines && maxLines > 1) {
      compare = current && current.scrollHeight > current.clientHeight;
    }
    if (compare) {
      setHover(compare);
    }
  }, [maxLines]);

  useEffect(() => {
    compareSize();
    window.addEventListener('resize', compareSize);
  }, [compareSize]);

  useEffect(
    () => () => {
      window.removeEventListener('resize', compareSize);
    },
    [compareSize],
  );

  return (
    <Tooltip title={text || ''} disableHoverListener={!hover} arrow>
      <div
        ref={textElementRef as React.LegacyRef<HTMLDivElement> | undefined}
        className={cx(maxLines === 1 ? classes.maxOneLine : classes.multipleLines, className)}
        style={{ WebkitLineClamp: maxLines }}
      >
        {children}
      </div>
    </Tooltip>
  );
};

export default OverflowTooltip;
